import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import GamifiedButton from '../components/common/GamifiedButton';
import SEO from '../components/SEO';
import PhotoGallery from '../components/PhotoGallery';
import { packPhoto, packPhotoSmall } from '../lib/packPhotos';
import { useCart } from '../contexts/CartContext';
import { getProducts } from '../services/api';
import { useLang } from '../i18n/LanguageContext';
import { formaterPrix } from '../lib/prix';

// Image URLs are now fetched from database via product.imageUrl

export default function Shop() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { lang, t, tp, tpList } = useLang();

  // Ces deux aides vivaient au niveau du module ; elles descendent dans le
  // composant car les textes produit passent par tp/tpList (des hooks).

  // Use description from database, with fallback for legacy products
  const getDescriptionForProduct = (pack) => {
    // Use the real description from the database
    if (pack.description && pack.description.trim().length > 0) {
      return tp(pack, 'description', pack.description);
    }

    // Fallback only if no description in DB (shouldn't happen)
    return t('shop.produit.descriptionRepli');
  };

  // Use features from database if available, otherwise generate from pack type
  const getFeaturesForProduct = (pack) => {
    // If features exist in DB, use them
    if (pack.features && Array.isArray(pack.features) && pack.features.length > 0) {
      return tpList(pack, 'features', pack.features);
    }

    // Fallback: generate features based on variant type
    const idStr = String(pack.id).toLowerCase();
    const name = (pack.name || '').toLowerCase();

    if (idStr.includes('duo') || name.includes('duo')) {
      return [
        t('shop.produit.repliDuo1'),
        t('shop.produit.repliDuo2'),
        t('shop.produit.repliDuo3')
      ];
    }
    if (idStr.includes('fam') || name.includes('famille')) {
      return [
        t('shop.produit.repliFamille1'),
        t('shop.produit.repliFamille2'),
        t('shop.produit.repliFamille3')
      ];
    }
    if (idStr.includes('prem') || name.includes('premium')) {
      return [
        t('shop.produit.repliPremium1'),
        t('shop.produit.repliPremium2'),
        t('shop.produit.repliPremium3')
      ];
    }
    if (idStr.includes('mixte') || pack.variantType === 'mixed') {
      return [
        t('shop.produit.repliMixte1'),
        t('shop.produit.repliMixte2'),
        t('shop.produit.repliMixte3')
      ];
    }

    // Default
    return [
      t('shop.produit.repliDefaut1'),
      t('shop.produit.repliDefaut2'),
      t('shop.produit.repliDefaut3')
    ];
  };
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [selectedProductForModal, setSelectedProductForModal] = useState(null);
  const [redirectAfterModal, setRedirectAfterModal] = useState(false);
  const [selectedPacks, setSelectedPacks] = useState(new Set());
  const [filterColor, setFilterColor] = useState('all');

  // La modale « détails » qui vivait ici faisait office de fiche produit dans
  // une fenêtre de 500 px. Elle est remplacée par la vraie page /product/:id,
  // qui peut tout montrer : visuels, contenu, fiche technique, règles, avis.



  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        const sorted = data.sort((a, b) => a.price - b.price);
        setProducts(sorted);
      } catch (error) {
        console.error('Failed to load products', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (products.length === 0) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': t('shop.schema.nom'),
      'description': t('shop.schema.description'),
      'url': 'https://www.mirumgames.com/shop',
      'mainEntity': {
        '@type': 'ItemList',
        'itemListElement': products.map((pack, idx) => ({
          '@type': 'Product',
          'position': idx + 1,
          'name': `${tp(pack, 'name', pack.name)} - Joyful Reveal`,
          'description': tp(pack, 'longDescription', pack.longDescription) || tp(pack, 'description', pack.description),
          'image': `https://www.mirumgames.com${packPhoto(pack)}`,
          'price': pack.price.toString(),
          'priceCurrency': 'EUR',
          'brand': {
            '@type': 'Brand',
            'name': 'Mirum Games'
          },
          'offers': {
            '@type': 'Offer',
            // Chaque offre pointe vers SA fiche : une URL commune pour tous les
            // packs empêche Google de les distinguer.
            'url': `https://www.mirumgames.com/product/${pack.id}`,
            'price': pack.price.toString(),
            'priceCurrency': 'EUR',
            'availability': pack.stockQuantity > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
          }
        }))
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => script.remove();
    // lang : le JSON-LD suit la langue affichée (t/tp dépendent d'elle).
  }, [products, lang]);

  const handleProductClick = (pack, shouldRedirect = false) => {
    if (pack.stockQuantity <= 0) return;

    // Check variant type
    if (pack.variantType === 'mixed' || pack.variantType === 'boy' || pack.variantType === 'girl') {
      let variant = 'mixed';
      let nameSuffix = '';

      if (pack.variantType === 'boy') { variant = 'boy'; nameSuffix = t('shop.panier.suffixeGarcon'); }
      else if (pack.variantType === 'girl') { variant = 'girl'; nameSuffix = t('shop.panier.suffixeFille'); }

      addToCart({
        id: pack.id,
        name: t('shop.panier.nomArticle', { nom: `${tp(pack, 'name', pack.name)}${nameSuffix}` }),
        price: pack.price,
        image: packPhoto(pack),
        weight: pack.weight || 3.0, // Use DB weight
        variant: variant,
        language: 'FR' // Default language
      });

      if (shouldRedirect) {
        navigate('/cart');
      } else {
        setSelectedPacks(prev => new Set(prev).add(pack.id));
        setTimeout(() => {
          setSelectedPacks(prev => {
            const newSet = new Set(prev);
            newSet.delete(pack.id);
            return newSet;
          });
        }, 2000);
      }
      return;
    }

    setSelectedProductForModal(pack);
    setRedirectAfterModal(shouldRedirect);
    setShowGenderModal(true);
  };

  const confirmAddToCart = (gender) => {
    if (!selectedProductForModal) return;

    const pack = selectedProductForModal;

    addToCart({
      id: pack.id,
      name: t('shop.panier.nomArticle', {
        nom: `${tp(pack, 'name', pack.name)}${gender === 'boy' ? t('shop.panier.suffixeGarcon') : t('shop.panier.suffixeFille')}`,
      }),
      price: pack.price,
      image: packPhoto(pack),
      weight: pack.weight || 1.5, // Use DB weight
      variant: gender,
      language: 'FR' // Default language
    });

    setShowGenderModal(false);
    setSelectedProductForModal(null);

    if (redirectAfterModal) {
      navigate('/cart');
    } else {
      setSelectedPacks(prev => new Set(prev).add(pack.id));
      setTimeout(() => {
        setSelectedPacks(prev => {
          const newSet = new Set(prev);
          newSet.delete(pack.id);
          return newSet;
        });
      }, 2000);
    }
  };

  // Plus de filtre : il y a deux packs. L'ancienne barre proposait six boutons
  // (« ⚡ Flash Sale », « 🌟 Popular », « 💑 Duo », « 👨‍👩‍👧‍👦 Famille »,
  // « 💜 Mixte ») qui triaient en réalité sur la couleur de thème du pack :
  // « Duo » affichait l'Essentiel, et trois boutons sur six menaient à une page
  // « Aucun pack trouvé ». Un visiteur en déduisait un catalogue de six produits
  // et en cherchait quatre qui n'existent pas.
  const filteredPacks = products;

  const colorClasses = {
    pink: 'border-[#F4A6C1]',
    mint: 'border-[#A8D5BA]',
    sky: 'border-[#A7D8F7]',
    warm: 'border-[#4AA294]',
    purple: 'border-[#A78BFA]',
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-text">{t('shop.page.chargement')}</div>;
  }

  return (
    <div className="bg-background">
      <SEO
        title={t('shop.seo.titre')}
        description={t('shop.seo.description')}
        url="/shop"
        keywords="puzzle révélation sexe bébé, packs révélation, cadeau grossesse, jeu gender reveal"
      />

      {/* Header Section */}
      <section className="py-15 px-5 bg-gradient-to-br from-[#F7F4EE] via-[#F3F2EA] to-[#F7F4EC] text-center animate-[fadeInUp_0.8s_ease-out]">
        <div className="max-w-[1000px] mx-auto">
          <span className="inline-block bg-gradient-to-br from-[#A8D5BA]/20 to-[#F4A6C1]/20 border-2 border-[#A8D5BA] text-[#17453E] px-4 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wide mb-4">
            {t('shop.entete.badge')}
          </span>
          <h1 className="m-0 mb-4 text-3xl sm:text-4xl md:text-5xl font-black text-[#33403B] uppercase tracking-tight break-words">
            {t('shop.entete.titre')}
          </h1>
          <p className="m-0 text-base text-[#33403B] leading-relaxed max-w-[700px] mx-auto">
            {t('shop.entete.texte')}
          </p>
        </div>
      </section>

      {/* Aide au choix — remplace la barre de filtres, inutile avec deux packs */}
      <section className="py-8 px-5 bg-gradient-to-br from-white to-[#F7F4EC] border-b-2 border-[#4AA294]">
        <div className="max-w-[820px] mx-auto text-center">
          <h2 className="m-0 mb-3 text-base font-bold text-[#33403B] uppercase tracking-wide">
            {t('shop.aide.titre')}
          </h2>
          <p className="m-0 text-[15px] leading-relaxed text-[#33403B]/80">
            {t('shop.aide.seg1')}<strong>{t('shop.aide.seg2')}</strong>{t('shop.aide.seg3')}<strong>{t('shop.aide.seg4')}</strong>{t('shop.aide.seg5')}
          </p>
        </div>
      </section>

      {/* Products Grid — la classe shop-products est la cible du bouton de bas
          de page, qui ne défilait nulle part faute d'élément portant ce nom. */}
      <section className="shop-products py-15 px-5 bg-gradient-to-br from-[#F7F4EE] via-[#F3F2EA] to-[#F7F4EC]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-[fadeInUp_0.8s_ease-out]">
            {filteredPacks.map(pack => (
              <div
                key={pack.id}
                className={`relative bg-gradient-to-br from-white to-[#F7F4EC] border-3 rounded-lg overflow-hidden shadow-md transition-all duration-300 flex flex-col h-full hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(139,111,71,0.2)] ${colorClasses[pack.color] || 'border-[#4AA294]'
                  } ${selectedPacks.has(pack.id) ? 'border-[#A8D5BA] animate-[bounceSoft_0.6s_ease-out]' : ''
                  } ${pack.stockQuantity <= 0 ? 'opacity-60' : ''
                  }`}
              >
                {/* Image */}
                <div className="relative w-full h-[220px] md:h-[160px] overflow-hidden bg-gradient-to-br from-[#A8D5BA]/10 to-[#F4A6C1]/10">
                  <img
                    src={packPhoto(pack)}
                    srcSet={`${packPhotoSmall(pack)} 600w, ${packPhoto(pack)} 1200w`}
                    sizes="(max-width: 768px) 90vw, 300px"
                    alt={t('shop.grille.altPhoto', { nom: tp(pack, 'name', pack.name) })}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-black/20"></div>
                  {/* Only show badge if out of stock OR if there's a promo */}
                  {(pack.stockQuantity <= 0 || (pack.originalPrice && Number(pack.originalPrice) > Number(pack.price))) && (
                    <div className="absolute top-3 right-3 bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white px-3 py-2 rounded text-xs font-bold uppercase tracking-wide">
                      {pack.stockQuantity <= 0
                        ? t('shop.grille.rupture')
                        : `-${Math.round(((Number(pack.originalPrice) - Number(pack.price)) / Number(pack.originalPrice)) * 100)}%`
                      }
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <div className="text-[11px] font-bold text-[#2F7E72] uppercase tracking-wide">
                    {pack.stockQuantity <= 0 ? t('shop.grille.bientotRetour') : t('shop.grille.enStock')}
                  </div>
                  <h3 className="m-0 text-base font-bold text-[#33403B]">
                    {tp(pack, 'name', pack.name)}
                  </h3>

                  {/* Price */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {pack.originalPrice && (
                      <span className="text-xs text-[#6C7B75] line-through">
                        {formaterPrix(pack.originalPrice, lang)}
                      </span>
                    )}
                    <div className="text-[22px] font-black text-[#2F7E72]">
                      {formaterPrix(pack.price, lang)}
                    </div>
                  </div>

                  <p className="m-0 text-xs text-[#33403B] leading-relaxed">
                    {getDescriptionForProduct(pack).length > 100
                      ? getDescriptionForProduct(pack).substring(0, 100) + '...'
                      : getDescriptionForProduct(pack)}
                    {getDescriptionForProduct(pack).length > 100 && (
                      <Link
                        to={`/product/${pack.id}`}
                        data-cta={`shop:fiche-${pack.id}`}
                        className="ml-1 text-[#2F7E72] font-bold underline cursor-pointer hover:text-[#4AA294]"
                      >
                        {t('shop.grille.voirPlus')}
                      </Link>
                    )}
                  </p>

                  {/* Features */}
                  <ul className="list-none m-0 p-0 flex flex-col gap-1.5 text-[11px] text-[#33403B]">
                    {getFeaturesForProduct(pack).slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="font-medium">{feature}</li>
                    ))}
                    {getFeaturesForProduct(pack).length > 3 && (
                      <li className="text-[#2F7E72] italic">
                        {t('shop.grille.autresCaracteristiques', { n: getFeaturesForProduct(pack).length - 3 })}
                      </li>
                    )}
                  </ul>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button
                      data-cta={`shop:add-${pack.id}`}
                      onClick={() => handleProductClick(pack, false)}
                      disabled={pack.stockQuantity <= 0}
                      className="bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white border-none px-3 py-2.5 rounded text-xs font-semibold uppercase tracking-wide cursor-pointer transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_6px_15px_rgba(139,111,71,0.3)] hover:-translate-y-0.5"
                    >
                      {pack.stockQuantity <= 0 ? t('shop.grille.epuise') : (selectedPacks.has(pack.id) ? t('shop.grille.ajoute') : t('shop.grille.ajouter'))}
                    </button>
                    <button
                      data-cta={`shop:buy-${pack.id}`}
                      onClick={() => handleProductClick(pack, true)}
                      disabled={pack.stockQuantity <= 0}
                      className="bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white border-none px-3 py-2.5 rounded text-xs font-semibold uppercase tracking-wide cursor-pointer transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_6px_15px_rgba(139,111,71,0.3)] hover:-translate-y-0.5"
                    >
                      {t('shop.grille.versPanier')}
                    </button>
                  </div>

                  {/* Entrée vers la fiche produit. Sans ce lien, la page
                      /product/:id resterait invisible pour tout le monde. */}
                  <Link
                    to={`/product/${pack.id}`}
                    data-cta={`shop:fiche-complete-${pack.id}`}
                    className="mt-2 block rounded border-2 border-[#4AA294]/40 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-[#2F7E72] no-underline transition-all hover:border-[#2F7E72] hover:bg-[#4AA294]/10"
                  >
                    {t('shop.grille.ficheComplete')}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {filteredPacks.length === 0 && (
            <div className="text-center py-20 px-10 bg-gradient-to-br from-[#4AA294]/10 to-[#4AA294]/5 border-2 border-dashed border-[#4AA294] rounded-lg animate-[fadeInUp_0.5s_ease-out]">
              <div className="text-[64px] mb-4 inline-block">🔍</div>
              <h3 className="m-0 mb-2 text-2xl font-bold text-[#33403B]">
                {t('shop.vide.titre')}
              </h3>
              <p className="m-0 text-sm text-[#33403B]">
                {t('shop.vide.texte')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 px-5 bg-gradient-to-br from-white to-[#F7F4EC] border-t-3 border-[#4AA294]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="m-0 mb-15 text-3xl md:text-4xl font-black text-[#33403B] uppercase tracking-tight text-center">
            {t('shop.pourquoi.titre')}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🎁', title: t('shop.pourquoi.emballageTitre'), text: t('shop.pourquoi.emballageTexte'), delay: '0s' },
              { icon: '🎮', title: t('shop.pourquoi.jeuTitre'), text: t('shop.pourquoi.jeuTexte'), delay: '0.2s' },
              { icon: '🧩', title: t('shop.pourquoi.assemblageTitre'), text: t('shop.pourquoi.assemblageTexte'), delay: '0.4s' },
              { icon: '💝', title: t('shop.pourquoi.momentTitre'), text: t('shop.pourquoi.momentTexte'), delay: '0.6s' },
              // Ces deux promesses étaient fausses : le colis part de Belgique,
              // pas de France, et les CGV du site accordent 14 jours de
              // rétractation, pas 30 jours « sans questions ».
              { icon: '📦', title: t('shop.pourquoi.expeditionTitre'), text: t('shop.pourquoi.expeditionTexte'), delay: '0.8s' },
              { icon: '✨', title: t('shop.pourquoi.retractationTitre'), text: t('shop.pourquoi.retractationTexte'), delay: '1s' },
            ].map((card, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-[#F7F4EE] to-[#F3F2EA] border-2 border-[#4AA294] rounded-lg py-8 px-6 text-center shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_15px_40px_rgba(139,111,71,0.2)]"
              >
                <div
                  className="text-5xl mb-4 inline-block animate-[floatGentle_2s_ease-in-out_infinite]"
                  style={{ animationDelay: card.delay }}
                >
                  {card.icon}
                </div>
                <h3 className="m-0 mb-3 text-[15px] font-bold text-[#33403B]">
                  {card.title}
                </h3>
                <p className="m-0 text-[13px] leading-relaxed text-[#33403B]">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photos réelles du produit */}
      <PhotoGallery />

      {/* CTA Section */}
      <section className="py-20 px-5 bg-gradient-to-br from-[#F7F4EE] via-[#F3F2EA] to-[#F7F4EC] text-center border-t-3 border-[#4AA294]">
        <div className="max-w-[800px] mx-auto">
          <h2 className="m-0 mb-4 text-3xl md:text-4xl sm:text-2xl font-black text-[#33403B] uppercase tracking-tight">
            {t('shop.cta.titre')}
          </h2>
          <p className="m-0 mb-8 text-base text-[#33403B] leading-relaxed">
            {t('shop.cta.texte')}
          </p>
          <GamifiedButton
            variant="primary"
            size="lg"
            onClick={() => document.querySelector('.shop-products')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t('shop.cta.bouton')}
          </GamifiedButton>
        </div>
      </section>

      {/* Gender Selection Modal */}
      {showGenderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-[fadeIn_0.3s_ease-out]" onClick={() => setShowGenderModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full animate-[fadeInUp_0.4s_ease-out]" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <h3 className="text-2xl font-black text-[#33403B] uppercase mb-2">{t('shop.modale.titre')}</h3>
              <p className="text-[#2F7E72]">{t('shop.modale.sousTitre')}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                data-cta="shop:variante-garcon"
                onClick={() => confirmAddToCart('boy')}
                className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 hover:scale-105 transition-all duration-300 group"
              >
                <span className="text-4xl group-hover:scale-110 transition-transform">👦</span>
                <span className="font-bold text-blue-600 uppercase tracking-wide">{t('shop.modale.garcon')}</span>
              </button>

              <button
                data-cta="shop:variante-fille"
                onClick={() => confirmAddToCart('girl')}
                className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-pink-200 bg-pink-50 hover:bg-pink-100 hover:border-pink-400 hover:scale-105 transition-all duration-300 group"
              >
                <span className="text-4xl group-hover:scale-110 transition-transform">👧</span>
                <span className="font-bold text-pink-600 uppercase tracking-wide">{t('shop.modale.fille')}</span>
              </button>
            </div>

            <button
              data-cta="shop:variante-annuler"
              onClick={() => setShowGenderModal(false)}
              className="w-full py-3 text-gray-500 font-medium hover:text-gray-800 transition-colors"
            >
              {t('shop.modale.annuler')}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
